import type { PaymentOptions } from '../domain/types'
import defaultFetch from 'isomorphic-unfetch'
import {
  AdyenPaymentResponse,
  CreatePaymentParametersRequest,
  CreatePaymentParametersResponse,
  InitPaymentRequest,
  InitPaymentResponse,
  PaymentOptionsApi,
  SubmitAdditionalDetailsRequest,
  SubmitAdditionalDetailsResponse,
} from '@/scopes/payment/infrastructure/types'
import { mapPaymentOptions } from './paymentOptionsMapper'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  contextItemId: string
  groupUserId: string | null
}

function parsePaymentResponse(response: AdyenPaymentResponse | string): AdyenPaymentResponse {
  if (typeof response === 'string') {
    return JSON.parse(response) as AdyenPaymentResponse
  }

  return response
}

export function createPaymentApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    contextItemId = '',
    groupUserId = null,
  } = dependencies as Dependencies

  async function getPaymentOptions(bookingNumber: string): Promise<PaymentOptions> {
    const response = await fetch(buildPaymentOptionsUrl(bookingNumber, contextItemId, groupUserId))

    if (!response.ok) {
      throw new Error(`Failed to fetch payment options: ${response.status} ${response.statusText}`)
    }

    const data: PaymentOptionsApi = await response.json()
    return mapPaymentOptions(data)
  }

  async function createPaymentParameters(
    body: CreatePaymentParametersRequest,
    signal?: AbortSignal,
  ): Promise<CreatePaymentParametersResponse> {
    const response = await fetch(buildPaymentUrl('CreatePaymentParameters'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal,
    })

    if (!response.ok) {
      throw new Error(`Failed to create payment parameters: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as CreatePaymentParametersResponse

    return {
      ...data,
      adyen: { ...(data.adyen ?? {}), countryCode: getCountryFromLocale(data?.adyen?.locale as string) }, // TODO: add missingCountryCode on backend?
    }
  }

  function getCountryFromLocale(locale: string): string | null {
    const parts = locale.split('_')
    return parts.length > 1 ? parts[1].toUpperCase() : null
  }

  async function initPayment(body: InitPaymentRequest): Promise<AdyenPaymentResponse> {
    const response = await fetch(buildPaymentUrl('InitialPayment'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      throw new Error(`Failed to initiate payment: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as InitPaymentResponse
    return parsePaymentResponse(data.adyenPaymentResponse)
  }

  async function submitAdditionalDetails(body: SubmitAdditionalDetailsRequest): Promise<AdyenPaymentResponse> {
    const response = await fetch(buildPaymentUrl('SubmitAdditionalDetails'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      throw new Error(`Failed to submit additional details: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as SubmitAdditionalDetailsResponse
    return parsePaymentResponse(data.adyenPaymentResponse)
  }

  function buildPaymentOptionsUrl(bookingNumber: string, contextItemId: string, groupUserId: string | null) {
    const queryString = new URLSearchParams({
      bookingNumber,
      contextItemId,
    })
    if (groupUserId) {
      queryString.set('groupUserId', groupUserId)
    }

    return `${env.NEXT_PUBLIC_API_BASE_URL}/PaymentApi/GetPaymentOptions?${queryString}`
  }

  function buildPaymentUrl(action: 'CreatePaymentParameters' | 'InitialPayment' | 'SubmitAdditionalDetails') {
    return `${env.NEXT_PUBLIC_API_BASE_URL}/PaymentApi/${action}`
  }

  return {
    getPaymentOptions,
    createPaymentParameters,
    initPayment,
    submitAdditionalDetails,
  }
}
